/**
 * DecisionMakingPanel — TB-2 (AC-DM-04, AC-DM-05, AC-DM-06, AC-DM-07-full)
 *
 * Upgraded from TB-1 read-only panel:
 *  - Managers (roros.governance.manage) can edit: hybrid Deciders editor
 *    (chips from org Actors/Roles + free-text custom), dropdown Method,
 *    and Notes text input.
 *  - Members see all fields read-only (chips, plain text).
 *  - Save persists; reload shows saved values.
 *
 * F-04: uses shared types from @/types/roros/decisions-shared.
 * F-03: editing apparatus imported from DecidersEditor (old DecisionRightsPage.tsx
 *        is now deleted — this panel is the single source of truth).
 * #3: IMPACT_LEVELS computed via useMemo so t() re-evaluates on locale change.
 * #4: Impact-level strings (definitions + examples) seeded EN+DE in migration 595.
 *
 * PRD Decision Making §6.1 + §6.2 + AC-DM-04/05/06/07.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Info, Save } from 'lucide-react';
import { t } from '@/lib/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { QueryPage } from '@/components/QueryPage';
import { showToast } from '@/components/ui/Toast';
import { DecisionMethodLibraryPanel } from '@/components/roros/decisions/DecisionMethodLibraryPanel';
import { DecidersEditor } from '@/components/roros/decisions/DecidersEditor';
import {
  useRorosDecisionRights,
  useRorosDecisionRightsUpdate,
} from '@/hooks/roros/governance';
import type {
  Decider,
  DecisionRightsLevel,
  DecisionRightsMatrix,
  ImpactLevel,
} from '@/types/roros/decisions-shared';
import { DEFAULT_MATRIX } from '@/types/roros/decisions-shared';

// ─── Method dropdown options (locale-reactive — built at component scope) ─────
//
// Intentionally NOT at module scope: t() must be called per-render so that
// labels update when the user switches locale without a full page reload.
// Values match the 7 slugs in RorosDecisionMethod / DECISION_MODES (server).

function buildModeOptions() {
  return [
    { value: 'autocratic',             label: t('Autocratic') },
    { value: 'consultative',           label: t('Consultative') },
    { value: 'consent_based',          label: t('Consent-based') },
    { value: 'consensus',              label: t('Consensus') },
    { value: 'delegation',             label: t('Delegation') },
    { value: 'voting',                 label: t('Voting') },
    { value: 'deliberative_alignment', label: t('Deliberative Alignment') },
  ];
}

// ─── Impact levels — locale-reactive (#3) ────────────────────────────────────

function buildImpactLevels() {
  return [
    {
      key: 'root' as ImpactLevel,
      label: t('Root'),
      definition: t('Existential decisions — alter the company\'s fundamental direction'),
      example: t('e.g. "Pivot the company"'),
      badgeVariant: 'error' as const,
    },
    {
      key: 'trunk' as ImpactLevel,
      label: t('Trunk'),
      definition: t('Strategic decisions — shape the next 6–12 months'),
      example: t('e.g. "Choose the next product to build"'),
      badgeVariant: 'warning' as const,
    },
    {
      key: 'branch' as ImpactLevel,
      label: t('Branch'),
      definition: t('Tactical decisions — affect a domain or team'),
      example: t('e.g. "Switch CRM vendor"'),
      badgeVariant: 'secondary' as const,
    },
    {
      key: 'leaf' as ImpactLevel,
      label: t('Leaf'),
      definition: t('Operational decisions — day-to-day'),
      example: t('e.g. "Buy software for $200"'),
      badgeVariant: 'default' as const,
    },
  ];
}

/**
 * #3: locale-reactive IMPACT_LEVELS — built inside the component render so
 * t() re-evaluates on every locale change rather than at module import time.
 */
function useImpactLevels() {
  // Re-build on every render (t() is synchronous + cheap; memo would capture
  // a stale locale snapshot across i18n context changes).
  return buildImpactLevels();
}

// ─── Level row ───────────────────────────────────────────────────────────────

interface LevelRowProps {
  level: ReturnType<typeof useImpactLevels>[number];
  value: DecisionRightsLevel;
  canManage: boolean;
  onChange: (level: ImpactLevel, updated: DecisionRightsLevel) => void;
}

function LevelRow({ level, value, canManage, onChange }: LevelRowProps) {
  const handleDecidersChange = useCallback(
    (next: Decider[]) => onChange(level.key, { ...value, actors: next }),
    [level.key, value, onChange],
  );

  // Build per-render so t() is locale-reactive (SEC-04 + mode dropdown locale fix)
  const modeOptions = buildModeOptions();

  return (
    <tr className="border-b border-border last:border-0">
      {/* Impact level — AC-DMPW-1: w-56→w-64 (256px) */}
      <td className="py-3 pr-4 align-top w-64">
        <div className="space-y-0.5">
          <Badge variant={level.badgeVariant}>{level.label}</Badge>
          <p className="text-xs text-muted-foreground mt-1 leading-snug">{level.definition}</p>
          <p className="text-xs text-muted-foreground/70 italic">{level.example}</p>
        </div>
      </td>

      {/* Deciders — AC-DMPW-1: fixed w-40 (160px) so Notes (flex) is wider; chips wrap */}
      <td className="py-3 pr-4 align-top w-40">
        <DecidersEditor
          value={value.actors}
          onChange={canManage ? handleDecidersChange : undefined}
          canManage={canManage}
          levelLabel={level.label}
        />
      </td>

      {/* Suggested Method */}
      <td className="py-3 pr-4 align-top w-48">
        {canManage ? (
          <Select
            options={modeOptions}
            value={value.mode}
            onValueChange={(v) => onChange(level.key, { ...value, mode: v })}
            aria-label={t('{{level}} decision method').replace('{{level}}', level.label)}
          />
        ) : (
          <span className="text-sm text-foreground capitalize">
            {value.mode.replace(/_/g, ' ')}
          </span>
        )}
      </td>

      {/* Notes */}
      <td className="py-3 align-top">
        {canManage ? (
          <Textarea
            value={value.notes ?? ''}
            onChange={(e) => onChange(level.key, { ...value, notes: e.target.value })}
            placeholder={t('Optional context')}
            aria-label={t('{{level}} notes').replace('{{level}}', level.label)}
            rows={3}
            className="w-full resize-y"
          />
        ) : (
          <span className="text-sm text-muted-foreground">{value.notes ?? '—'}</span>
        )}
      </td>
    </tr>
  );
}

// ─── Main panel ──────────────────────────────────────────────────────────────

export function DecisionMakingPanel() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_GOVERNANCE_MANAGE);

  const query = useRorosDecisionRights();
  const updateMutation = useRorosDecisionRightsUpdate();

  const [localMatrix, setLocalMatrix] = useState<DecisionRightsMatrix | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const impactLevels = useImpactLevels();

  // Sync local state when remote data loads (but not while editing — AC-DM-04)
  useEffect(() => {
    if (query.data && !isDirty) {
      // fetchJson() unwraps the API envelope → query.data IS the matrix.
      // Service coerces legacy string actors → Decider objects (AC-DM-05).
      const matrix = (query.data as unknown as DecisionRightsMatrix) ?? DEFAULT_MATRIX;
      setLocalMatrix(matrix);
    }
  }, [query.data, isDirty]);

  const handleChange = useCallback((level: ImpactLevel, updated: DecisionRightsLevel) => {
    setLocalMatrix((prev) => {
      if (!prev) return prev;
      return { ...prev, [level]: updated };
    });
    setIsDirty(true);
  }, []);

  const handleSave = () => {
    if (!localMatrix) return;
    updateMutation.mutate(localMatrix as unknown as Record<string, unknown>, {
      onSuccess: () => {
        setIsDirty(false);
        showToast.success(t('Decision Making guidance saved'));
      },
      onError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : t('Failed to save guidance');
        showToast.error(msg);
      },
    });
  };

  const handleReset = () => {
    // C-#8: always reset — fall back to DEFAULT_MATRIX if query.data is unavailable
    // (prevents dirty state from getting stuck when data is null/undefined)
    setLocalMatrix((query.data as unknown as DecisionRightsMatrix) ?? DEFAULT_MATRIX);
    setIsDirty(false);
  };

  const displayMatrix = localMatrix ?? DEFAULT_MATRIX;

  return (
    <div className="space-y-8 mt-4" data-testid="decision-making-panel">
      {/* Guidance intro — AC-DM-02 */}
      <Alert variant="info" title={t('Decision Making Guidance')}>
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden />
          <span>
            {t(
              'This content is guidance that informs the AI moderator and your team — it does not lock or enforce decisions. Your org can tailor each impact level\'s deciders, suggested method, and notes to reflect how you actually make decisions.',
            )}
          </span>
        </div>
      </Alert>

      {/* Read-only notice for non-managers — AC-DM-07 */}
      {!canManage && (
        <Alert variant="info" title={t('Read-only')}>
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 flex-shrink-0" aria-hidden />
            <span>{t('You need the Governance Manage permission to edit Decision Making guidance.')}</span>
          </div>
        </Alert>
      )}

      {/* Impact-level matrix — AC-DM-01/02/04 */}
      <QueryPage
        query={query}
        loadingText={t('Loading Decision Making guidance…')}
      >
        {() => (
          <Card className="overflow-x-auto">
            <div className="p-4">
              <h2 className="text-sm font-semibold text-foreground mb-1">
                {t('Impact-Level Decision Guidance')}
              </h2>
              <p className="text-xs text-muted-foreground mb-4">
                {t('Each impact level specifies guidance on who typically decides and which method is suggested.')}
              </p>
              <table
                className="w-full min-w-[820px] text-sm"
                aria-label={t('Decision Making Matrix')}
                data-testid="decision-making-matrix"
              >
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-foreground py-2 pr-4 w-64">
                      {t('Impact Level')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground py-2 pr-4 w-40">
                      {t('Deciders')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground py-2 pr-4 w-48">
                      {t('Suggested Method')}
                    </th>
                    <th className="text-left text-xs font-semibold text-muted-foreground py-2">
                      {t('Notes')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {impactLevels.map((level) => (
                    <LevelRow
                      key={level.key}
                      level={level}
                      value={displayMatrix[level.key] ?? DEFAULT_MATRIX[level.key]}
                      canManage={canManage}
                      onChange={handleChange}
                    />
                  ))}
                </tbody>
              </table>

              {/* Save / Reset actions for managers */}
              {canManage && isDirty && (
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-border">
                  <Button variant="outline" size="sm" onClick={handleReset}>
                    {t('Reset')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    isLoading={updateMutation.isPending}
                    data-testid="save-decision-making-btn"
                  >
                    <Save className="h-4 w-4 mr-1" aria-hidden />
                    {t('Save')}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        )}
      </QueryPage>

      {/* Decision Method Library — AC-DM-03: read-only, non-picker mode */}
      <div>
        <h2 className="text-sm font-semibold text-foreground mb-1">
          {t('Decision Method Library')}
        </h2>
        <p className="text-xs text-muted-foreground mb-4">
          {t('Reference guide for the 7 built-in decision methods. Use these as a guide when reviewing the Suggested Method column above.')}
        </p>
        {/* No onSelect prop = read-only, non-picker mode (AC-DM-03) */}
        <DecisionMethodLibraryPanel />
      </div>
    </div>
  );
}
